import qs from 'qs';
import { $profileStore } from '@lib/store';
import { PermissionUtils } from './permissionUtils';
import { TransferRecipientTypeEnum } from '@shared/api';
import DashboardIcon from '@assets/icons/dashboard.svg';
import AccountIcon from '@assets/icons/account.svg';
import PaymentIcon from '@assets/icons/payment.svg';
import CardIcon from '@assets/icons/card.svg';
import RewardsIcon from '@assets/icons/rewards.svg';
import ConciergeIcon from '@assets/icons/concierge.svg';
import TravelIcon from '@assets/icons/travel.svg';
import UsersIcon from '@assets/icons/users.svg';
import ReportIcon from '@assets/icons/report.svg';
import StatementIcon from '@assets/icons/statement.svg';
import IntegrationIcon from '@assets/icons/integration.svg';
import ChatIcon from '@assets/icons/chat.svg';

export class RoutePaths {
  static accounts = {
    path: 'accounts',
    onNavigate: () => '/accounts',
  };
  static account = {
    path: 'accounts/:id',
    onNavigate: (id: number) => `/accounts/${id}`,
  };
  static accountTransaction = {
    path: 'accounts/transactions/:id',
    onNavigate: (id: string, query?: { back?: string }) => `/accounts/transactions/${id}${qs.stringify(query, { addQueryPrefix: true })}`,
  };

  static alerts = {
    path: 'alerts',
    onNavigate: (query?: { id?: string }) => `/alerts${qs.stringify(query, { addQueryPrefix: true })}`,
  };

  static callback = {
    path: 'callback',
    onNavigate: () => '/callback',
  };

  static cards = {
    path: 'cards',
    onNavigate: () => '/cards',
  };
  static card = {
    path: 'cards/:id',
    onNavigate: (id: number) => `/cards/${id}`,
  };

  static concierge = {
    path: 'concierge',
    onNavigate: () => '/concierge',
  };
  static conciergeRequest = {
    path: 'concierge/:type',
    onNavigate: (type: string) => `/concierge/${type}`,
  };
  static conciergeOffer = {
    path: 'concierge/:type/:offerId',
    onNavigate: (type: string, offerId: string) => `/concierge/${type}/${offerId}`,
  };

  static dashboard = {
    path: '',
    onNavigate: () => '/',
  };

  static error = {
    path: 'error',
    onNavigate: () => '/error',
  };

  static integrations = {
    path: 'integrations',
    onNavigate: () => '/integrations',
  };
  static integration = {
    path: 'integrations/:id',
    onNavigate: (id: string) => `/integrations/${id}`,
  };

  static messages = {
    path: 'message-centre',
    onNavigate: () => '/message-centre',
  };

  static payments = {
    path: 'payments',
    onNavigate: () => '/payments',
  };
  static paymentsInsignia = {
    path: 'payments/insignia',
    onNavigate: (query?: { type: TransferRecipientTypeEnum; recipientId?: string }) =>
      `/payments/insignia${qs.stringify(query, { addQueryPrefix: true })}`,
  };
  static paymentsOther = {
    path: 'payments/other',
    onNavigate: (query?: { type: TransferRecipientTypeEnum; recipientId?: string }) =>
      `/payments/other${qs.stringify(query, { addQueryPrefix: true })}`,
  };

  static paymentsCard = {
    path: 'payments/card',
    onNavigate: () => '/payments/card',
  };
  static paymentsRecipient = {
    path: 'payments/recipient/:id',
    onNavigate: (id: string) => `/payments/recipient/${id}`,
  };
  static paymentRecipients = {
    path: 'payments/recipients',
    onNavigate: () => '/payments/recipients',
  };
  static paymentNewRecipients = {
    path: 'payments/recipients/new',
    onNavigate: () => '/payments/recipients/new',
  };

  static profile = {
    path: 'profile',
    onNavigate: () => '/profile',
  };

  static reports = {
    path: 'reports',
    onNavigate: () => '/reports',
  };

  static rewards = {
    path: 'rewards',
    onNavigate: () => '/rewards',
  };

  static signup = {
    path: 'signup',
    onNavigate: () => '/signup',
  };

  static success = {
    path: 'success',
    onNavigate: () => '/success',
  };

  static statements = {
    path: 'statements',
    onNavigate: () => '/statements',
  };

  static travel = {
    path: 'travel',
    onNavigate: () => '/travel',
  };
  static travelEnrol = {
    path: 'enrol',
    onNavigate: () => '/travel/enrol',
  };
  static travelMyRequests = {
    path: 'my-requests',
    onNavigate: () => '/travel/my-requests',
  };
  static travelNewRequest = {
    path: 'new-request',
    onNavigate: () => '/travel/new-request',
  };
  static travelMyRequest = {
    path: 'my-requests/:id',
    onNavigate: (id: string) => `/travel/my-requests/${id}`,
  };

  static users = {
    path: 'user-management',
    onNavigate: (query?: { successDeleted?: boolean }) => `/user-management${qs.stringify(query, { addQueryPrefix: true })}`,
  };
  static user = {
    path: 'user-management/:id',
    onNavigate: (id: string) => `/user-management/${id}`,
  };
}

export class RouteUtils {
  private static getProfile = () => $profileStore.store.getState().data;

  static getMenu() {
    const profile = this.getProfile();

    const menu = [
      { id: 1, name: 'sidebar.menu.dashboard', link: RoutePaths.dashboard.onNavigate(), Icon: <DashboardIcon /> },
      { id: 2, name: 'sidebar.menu.accounts', link: RoutePaths.accounts.onNavigate(), Icon: <AccountIcon /> },
      { id: 3, name: 'sidebar.menu.payments', link: RoutePaths.payments.onNavigate(), Icon: <PaymentIcon /> },
      { id: 4, name: 'sidebar.menu.cards', link: RoutePaths.cards.onNavigate(), Icon: <CardIcon /> },
      { id: 5, name: 'sidebar.menu.concierge', link: RoutePaths.concierge.onNavigate(), Icon: <ConciergeIcon /> },
      {
        id: 6,
        name: 'sidebar.menu.travel',
        link: profile?.enrolledInCorporateTravelServices ? RoutePaths.travelNewRequest.onNavigate() : RoutePaths.travel.onNavigate(),
        Icon: <TravelIcon />,
      },
      { id: 7, name: 'sidebar.menu.rewards', link: RoutePaths.rewards.onNavigate(), Icon: <RewardsIcon /> },
      { id: 8, name: 'sidebar.menu.userManagement', link: RoutePaths.users.onNavigate(), Icon: <UsersIcon /> },
      { id: 9, name: 'sidebar.menu.statements', link: RoutePaths.statements.onNavigate(), Icon: <StatementIcon /> },
      { id: 10, name: 'sidebar.menu.reports', link: RoutePaths.reports.onNavigate(), Icon: <ReportIcon /> },
      { id: 11, name: 'sidebar.menu.integrations', link: RoutePaths.integrations.onNavigate(), Icon: <IntegrationIcon /> },
      { id: 12, name: 'sidebar.menu.messageCentre', link: RoutePaths.messages.onNavigate(), Icon: <ChatIcon /> },
    ];

    return menu.filter((item) => {
      switch (item.link) {
        case RoutePaths.accounts.onNavigate():
          return PermissionUtils.viewAccountsPage();
        case RoutePaths.payments.onNavigate():
          return PermissionUtils.viewPaymentsPage();
        case RoutePaths.cards.onNavigate():
          return PermissionUtils.viewCardsPage();
        case RoutePaths.rewards.onNavigate():
          return PermissionUtils.viewRewardsPage();
        case RoutePaths.concierge.onNavigate():
          return PermissionUtils.viewConciergePage();
        case RoutePaths.travel.onNavigate():
        case RoutePaths.travelNewRequest.onNavigate():
          return PermissionUtils.viewCorporateTravelPage();
        case RoutePaths.users.onNavigate():
          return PermissionUtils.viewUserManagementPage();
        case RoutePaths.statements.onNavigate():
          return PermissionUtils.viewStatementsPage();
        case RoutePaths.reports.onNavigate():
          return PermissionUtils.viewReportsPage();
        case RoutePaths.integrations.onNavigate():
          return PermissionUtils.viewIntegrationsPage();
        case RoutePaths.messages.onNavigate():
          return PermissionUtils.viewMessagesPage();
        default:
          return true;
      }
    });
  }
}
